import { Button, IconButton, Snackbar, SnackbarOrigin } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useStore } from 'logic';
import { observer } from 'mobx-react-lite';
import CloseIcon from '@mui/icons-material/Close';
import { MESSAGE_TIME_LIMIT_MS } from 'logic/ui-snackbar';
import { SnackbarPosition } from '@creative-kit/shared';

// Convert our position string to MUI's anchorOrigin
const getAnchorOrigin = (position: SnackbarPosition): SnackbarOrigin => {
  switch (position) {
    case 'top-center':
      return { vertical: 'top', horizontal: 'center' };
    case 'top-right':
      return { vertical: 'top', horizontal: 'right' };
    case 'bottom-center':
      return { vertical: 'bottom', horizontal: 'center' };
    case 'bottom-left':
      return { vertical: 'bottom', horizontal: 'left' };
    case 'bottom-right':
    default:
      return { vertical: 'bottom', horizontal: 'right' };
  }
};

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    backdropFilter: 'blur(12px)',
    color: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
    fontSize: 14,
    minWidth: 280,
    animation: '$slideIn 0.2s ease-out',
  },
  '@keyframes slideIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-12px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  main: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    gap: theme.spacing(1.5),
  },
  action: {
    marginLeft: 'auto',
    opacity: 0.7,
    '&:hover': {
      opacity: 1,
    },
  },
  progress: {
    height: 3,
    position: 'relative',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  progressRemaining: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(76, 175, 80, 0.6)',
    transition: 'width 0.1s linear',
  },
  button: {
    fontSize: 13,
    padding: '4px 12px',
    minWidth: 'auto',
    opacity: 0.85,
    fontWeight: 500,
    '&:hover': {
      opacity: 1,
    },
  },
}));

export const AppSnackbar = observer(() => {
  const store = useStore();
  const uiSnackbarStore = store?.uiSnackbarStore;

  if (!uiSnackbarStore) return null;

  const {
    description,
    remainingTime,
    hasAction,
    hasAdditionalActions,
    actionText,
    additionalActions,
    runSnackbarAction,
    runAdditionalAction,
    reset,
    snackbarsEnabled,
    snackbarPosition,
  } = uiSnackbarStore;

  const styles = useStyles();

  // Add top offset when positioned at top to clear the header
  const isTopPosition = snackbarPosition.startsWith('top');

  return (
    <Snackbar
      open={!!remainingTime && snackbarsEnabled}
      anchorOrigin={getAnchorOrigin(snackbarPosition)}
      sx={isTopPosition ? { top: '64px !important' } : undefined}
    >
      <div className={styles.content}>
        <div className={styles.main}>
          <span>{description}</span>
          {hasAdditionalActions &&
            additionalActions.map((action) => (
              <Button
                key={action.text}
                variant="text"
                color="inherit"
                size="small"
                className={styles.button}
                onClick={() => runAdditionalAction(action.fn)}
              >
                {action.text}
              </Button>
            ))}
          {hasAction && (
            <Button
              variant="text"
              color="inherit"
              size="small"
              className={styles.button}
              onClick={runSnackbarAction}
            >
              {actionText}
            </Button>
          )}
          <div className={styles.action}>
            <IconButton size="small" color="inherit" onClick={reset}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </div>
        </div>
        <div className={styles.progress}>
          <div
            className={styles.progressRemaining}
            style={{
              width: `${(100 * (remainingTime || 0)) / MESSAGE_TIME_LIMIT_MS}%`,
            }}
          />
        </div>
      </div>
    </Snackbar>
  );
});
